import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCheck, ChevronDown, ChevronRight, Hash, MessageCircle, Plus, Search, Users, X } from "lucide-react";
import { useOnClickOutside } from "usehooks-ts";

import { AddTaskForm } from "features/comunications/components/AddTaskForm/AddTaskForm";
import { useStaffMembers } from "features/practice/hooks/useStaffMembers";

import chatWidgetStyles from "./chat-widget.module.css";
import { AddNewChat } from "./components/add-new-chat";
import { ChannelMembers } from "./components/channel-members";
import { ChatMessages } from "./components/chat-messages";
import { ChatSendMessageForm } from "./components/chat-send-message-form";
import { SearchMessages } from "./components/search-messages";
import { useCreateChatGroup,useGetChatGroups } from "./hooks/use-chat-groups";
import { useGetChats } from "./hooks/use-get-chats";
import { useMarkDmRead, useMarkGroupRead } from "./hooks/use-mark-chat-read";
import { useUnreadSummary } from "./hooks/use-unread-summary";
import {
  cardContentStyle,
  cardHeaderStyle,
  cardStyle,
  cardTitleStyle,
  chatContainerStyle,
  fixedChatButtonStyle,
  mainContentStyle,
  sidebarHeaderStyle,
  sidebarItemActiveStyle,
  sidebarItemStyle,
  sidebarSectionStyle,
  sidebarSectionTitleStyle,
  sidebarStyle,
  unreadBadgeStyle,
} from "./style";

// Parse a backend LocalDateTime — either an ISO-8601 local string or
// the legacy [y, mo, d, h, mi, s] array — into epoch ms. Used purely
// for sorting the sidebar by lastMessageAt.
const toEpochMs = (value) => {
  if (!value) return 0;
  if (Array.isArray(value)) {
    const [y, mo, d, h = 0, mi = 0, s = 0] = value;
    return new Date(y, (mo || 1) - 1, d || 1, h, mi, s).getTime();
  }
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

// A small fixed palette for avatar fallbacks. We hash on the staff id
// so a given person always lands on the same colour, which the user
// learns subconsciously over time.
const AVATAR_PALETTE = [
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
  "#14b8a6",
  "#6366f1",
];

const avatarColorFor = (id) => {
  if (id == null) return AVATAR_PALETTE[0];
  const n = Number(id);
  if (!Number.isFinite(n)) return AVATAR_PALETTE[0];
  return AVATAR_PALETTE[Math.abs(n) % AVATAR_PALETTE.length];
};

const initialsFor = (staff) => {
  if (!staff) return "?";
  const first = (staff.firstName || "").trim();
  const last = (staff.lastName || "").trim();
  const a = first.charAt(0).toUpperCase();
  const b = last.charAt(0).toUpperCase();
  return `${a}${b}` || a || b || "?";
};

// Compact "5m ago" / "2h ago" / "3d ago" / "DD MMM" formatter used in
// the DM row right-hand column. Avoids dragging in moment for one line.
const formatRelativeTime = (ts) => {
  if (!ts) return "";
  const date =
    typeof ts === "number"
      ? new Date(ts)
      : Array.isArray(ts)
      ? new Date(
          ts[0],
          (ts[1] || 1) - 1,
          ts[2] || 1,
          ts[3] || 0,
          ts[4] || 0,
          ts[5] || 0
        )
      : new Date(ts);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "now";
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d`;
  return date.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
};

const previewTextFor = (msg) => {
  if (!msg?.message) return "";
  // Collapse whitespace and trim so multi-line messages render cleanly
  // in the single-line preview slot.
  return msg.message.replace(/\s+/g, " ").trim();
};

export function ChatWidget() {
  const [currentChatId, setCurrentChatId] = useState("");
  const [currentChannelId, setCurrentChannelId] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [currentView, setCurrentView] = useState("welcome"); // welcome, chat, channel, search
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [showAddChat, setShowAddChat] = useState(false);
  const [showAddChannel, setShowAddChannel] = useState(false);
  const [newChannelName, setNewChannelName] = useState("");
  const [showChannelMembers, setShowChannelMembers] = useState(false);
  const [channelsExpanded, setChannelsExpanded] = useState(true);
  // Task panel state - stores the message data to create a task from
  const [taskPanelData, setTaskPanelData] = useState(null);
  const [messagesExpanded, setMessagesExpanded] = useState(true);
  const [staffFilter, setStaffFilter] = useState("");
  // When true, hide the Active / Inactive buckets so the user sees
  // only conversations with unread messages — a manual escape hatch
  // if "Mark all read" can't clear everything in one go.
  const [unreadOnly, setUnreadOnly] = useState(false);
  const chatRef = useRef(null);
  const floatingButtonRef = useRef(null);

  const { data: savedChats } = useGetChats();
  const { data: chatGroups } = useGetChatGroups();
  const { data: allStaffMembers } = useStaffMembers();
  const { data: unreadSummary } = useUnreadSummary();
  const markDmRead = useMarkDmRead();
  const markGroupRead = useMarkGroupRead();
  const createChannel = useCreateChatGroup();

  const [newChat, setNewChat] = useState();

  const addNewChat = (staffMember) => {
    setNewChat({
      staffMember: staffMember,
      staffId: staffMember.id,
      chats: [],
    });
    setShowAddChat(false);
    handleSelectChat(staffMember.id);
  };

  // Show every staff record on the left except soft-deleted ones.
  // `active === false` means "off roster" but the person still exists
  // and should be reachable, so we deliberately do NOT exclude them —
  // the user explicitly asked for "all doctors" on the left. Only
  // `disabled === true` (the DBO soft-delete flag) drops them out.
  const isChatableStaff = (s) => s && s.disabled !== true;

  // Merge all staff members with existing saved chats so everyone shows in DM list.
  const chats = useMemo(() => {
    const chatMap = new Map();

    (savedChats || []).forEach((chat) => {
      if (!isChatableStaff(chat.staffMember)) return;
      chatMap.set(chat.staffId, chat);
    });

    if (
      newChat &&
      !chatMap.has(newChat.staffId) &&
      isChatableStaff(newChat.staffMember)
    ) {
      chatMap.set(newChat.staffId, newChat);
    }

    (allStaffMembers || []).forEach((staff) => {
      if (!isChatableStaff(staff)) return;
      if (!chatMap.has(staff.id)) {
        chatMap.set(staff.id, {
          staffMember: staff,
          staffId: staff.id,
          chats: [],
        });
      }
    });

    const list = Array.from(chatMap.values());

    // Three explicit sidebar buckets so a non-tech user can scan the
    // list quickly: anything with unread first, then currently-online
    // staff (alpha), then everyone else (alpha). "Online" is the
    // roster `active` flag, which the server maintains from each
    // user's presence heartbeat — `disabled` is the separate "is this
    // account enabled" flag.
    const isOnlineStaff = (chat) => chat.staffMember?.active === true;
    const nameOf = (chat) =>
      `${chat.staffMember?.firstName || ""} ${chat.staffMember?.lastName || ""}`
        .trim()
        .toLowerCase();
    const hasUnreadFlag = (chat) =>
      (chat.unreadCount || 0) > 0 || !!chat.hasUnreadMessages;

    const unread = list.filter(hasUnreadFlag);
    const remainder = list.filter((c) => !hasUnreadFlag(c));
    const active = remainder.filter(isOnlineStaff);
    const inactive = remainder.filter((c) => !isOnlineStaff(c));

    // Unread bucket: newest server-side lastMessageAt first so the
    // freshest pings are at the very top.
    unread.sort((a, b) => {
      const tDiff = toEpochMs(b.lastMessageAt) - toEpochMs(a.lastMessageAt);
      if (tDiff !== 0) return tDiff;
      return nameOf(a).localeCompare(nameOf(b));
    });
    active.sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
    inactive.sort((a, b) => nameOf(a).localeCompare(nameOf(b)));

    return { unread, active, inactive, all: [...unread, ...active, ...inactive] };
  }, [savedChats, newChat, allStaffMembers]);

  // Filtered sections for the sidebar — staffFilter narrows each
  // bucket independently so the section headers stay visible even
  // when a search hides everyone in one of them.
  const filteredChats = useMemo(() => {
    const empty = { unread: [], active: [], inactive: [] };
    if (!chats) return empty;
    const query = staffFilter.trim().toLowerCase();
    const apply = (bucket) =>
      !query
        ? bucket
        : bucket.filter((chat) => {
            const name = `${chat.staffMember?.firstName || ""} ${chat.staffMember?.lastName || ""}`.toLowerCase();
            return name.includes(query);
          });

    return {
      unread: apply(chats.unread),
      active: unreadOnly ? [] : apply(chats.active),
      inactive: unreadOnly ? [] : apply(chats.inactive),
    };
  }, [chats, staffFilter, unreadOnly]);

  // Floating-button badge reads straight from /chat/unread/summary so
  // it stays accurate even when the widget is closed and the per-list
  // polls aren't running.
  const totalUnreadCount = unreadSummary?.totalUnread || 0;

  // Without the button-ref guard, clicking the floating button while
  // the panel was open would fire both useOnClickOutside (closes) and
  // the button's onClick (toggles) in the same tick — the explicit
  // toggle then runs against stale state. Skipping clicks that target
  // the button lets the onClick own open/close cleanly.
  //
  // Also skip clicks on MUI Autocomplete poppers — they're portaled to
  // document.body so they appear "outside" the chat ref, but we don't
  // want selecting an autocomplete option to close the chat.
  useOnClickOutside(chatRef, (event) => {
    if (floatingButtonRef.current?.contains(event.target)) return;
    // Check if click is inside an MUI popper (autocomplete dropdown)
    const muiPopper = event.target.closest(".MuiAutocomplete-popper, .MuiPopper-root, .MuiPickersPopper-root");
    if (muiPopper) return;
    setIsOpen(false);
  });

  const toggleOpen = () => setIsOpen((previous) => !previous);

  const handleSelectChat = (chatId) => {
    setCurrentChatId(chatId);
    setCurrentChannelId("");
    setCurrentView("chat");
    setIsSearchOpen(false);
    // Fire-and-forget mark-read. The mutation optimistically zeroes
    // the badge for this conversation, so the sidebar updates before
    // the network roundtrip resolves. 403/404 are silent no-ops.
    if (chatId) markDmRead.mutate({ partnerStaffId: chatId });
  };

  const handleSelectChannel = (channelId) => {
    setCurrentChannelId(channelId);
    setCurrentChatId("");
    setCurrentView("channel");
    setIsSearchOpen(false);
    setShowChannelMembers(false);
    if (channelId) markGroupRead.mutate({ groupId: channelId });
  };

  // Fan-out across the existing per-conversation mark-read mutations
  // — there's no single endpoint, but each mutation optimistically
  // zeroes its row so the sidebar reacts instantly.
  //
  // We prefer the already-known unread buckets (one POST per
  // conversation that's actually unread). If those are empty but the
  // server-side summary still reports unread — which happens when the
  // per-conversation `unreadCount` field is missing/stale — fall back
  // to fanning out across every DM and every group. Mark-read is
  // idempotent and forward-only, so the extra POSTs are no-ops.
  const handleMarkAllRead = () => {
    const dmTargets = (chats?.unread || []).map((chat) => chat.staffId);
    const groupTargets = (chatGroups || [])
      .filter(
        (group) =>
          (group?.unreadCount || 0) > 0 || !!group?.hasUnreadMessages
      )
      .map((group) => group.id);

    const summaryStillUnread = (unreadSummary?.totalUnread || 0) > 0;
    const nothingToClear =
      dmTargets.length === 0 && groupTargets.length === 0;

    const finalDmTargets =
      nothingToClear && summaryStillUnread
        ? (chats?.all || []).map((chat) => chat.staffId)
        : dmTargets;
    const finalGroupTargets =
      nothingToClear && summaryStillUnread
        ? (chatGroups || []).map((group) => group.id)
        : groupTargets;

    finalDmTargets.forEach((staffId) => {
      if (staffId) markDmRead.mutate({ partnerStaffId: staffId });
    });
    finalGroupTargets.forEach((groupId) => {
      if (groupId) markGroupRead.mutate({ groupId });
    });
  };

  const handleCreateChannel = async () => {
    if (!newChannelName.trim()) return;

    try {
      const result = await createChannel.mutateAsync({
        name: newChannelName.trim(),
        description: "",
      });
      setNewChannelName("");
      setShowAddChannel(false);
      // Select the newly created channel
      if (result?.id) {
        handleSelectChannel(result.id);
      }
    } catch (error) {
      // Error handled by hook
    }
  };

  const currentChatPartner = chats?.all?.find(
    (chat) => chat.staffId === currentChatId
  )?.staffMember;

  const currentChannel = chatGroups?.find(
    (group) => group.id === currentChannelId
  );

  const getHeaderTitle = () => {
    if (isSearchOpen) return "Search Messages";
    if (currentView === "chat") return `${currentChatPartner?.firstName || ""} ${currentChatPartner?.lastName || ""}`;
    if (currentView === "channel") return `# ${currentChannel?.name || "Channel"}`;
    return "Team Chat";
  };

  return (
    <>
      <button
        ref={floatingButtonRef}
        style={fixedChatButtonStyle}
        onClick={toggleOpen}
        aria-label={isOpen ? "Close chat" : "Open chat"}
        aria-expanded={isOpen}
        onMouseEnter={(e) => {
          // Tactile lift — bigger shadow + slight scale on hover so the
          // button rewards interaction without being noisy.
          e.currentTarget.style.transform = "translateY(-1px) scale(1.04)";
          e.currentTarget.style.boxShadow =
            "0 16px 32px -10px rgba(0, 101, 70, 0.5), 0 6px 12px -3px rgba(0, 0, 0, 0.18)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "";
          e.currentTarget.style.boxShadow = fixedChatButtonStyle.boxShadow;
        }}
        onMouseDown={(e) => {
          e.currentTarget.style.transform = "translateY(0) scale(0.97)";
        }}
        onMouseUp={(e) => {
          e.currentTarget.style.transform = "translateY(-1px) scale(1.04)";
        }}
      >
        <MessageCircle
          style={{ height: "20px", width: "20px" }}
          strokeWidth={2.5}
        />
        {!isOpen && totalUnreadCount > 0 && (
          <span
            className={chatWidgetStyles.floatingButtonBadge}
            aria-label={`${totalUnreadCount} unread chat ${
              totalUnreadCount === 1 ? "message" : "messages"
            }`}
          >
            {totalUnreadCount > 99 ? "99+" : totalUnreadCount}
          </span>
        )}
      </button>
      {isOpen &&
        createPortal(
          <div
            ref={chatRef}
            style={{
              ...chatContainerStyle,
              transform: isOpen ? "translateY(0)" : "translateY(100%)",
            }}
          >
            <div style={cardStyle}>
              {/* Sidebar */}
              <div style={sidebarStyle}>
                {/* Sidebar Header */}
                <div style={sidebarHeaderStyle}>
                  <span style={{ color: "#fff", fontWeight: 700, fontSize: "1.125rem" }}>
                    Kinesin
                  </span>
                  <button
                    onClick={() => setIsSearchOpen(!isSearchOpen)}
                    style={{
                      border: "none",
                      backgroundColor: "transparent",
                      color: "#9ca3af",
                      cursor: "pointer",
                      padding: "4px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Search style={{ height: "16px", width: "16px" }} />
                  </button>
                </div>

                {(totalUnreadCount > 0 || unreadOnly) && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "8px",
                      padding: "8px 12px",
                      backgroundColor: "rgba(16, 185, 129, 0.12)",
                      borderBottom: "1px solid #35373b",
                      color: "#d1fae5",
                      fontSize: "0.8125rem",
                    }}
                  >
                    <span>
                      {totalUnreadCount} unread{" "}
                      {totalUnreadCount === 1 ? "message" : "messages"}
                    </span>
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setUnreadOnly((value) => !value)}
                        aria-pressed={unreadOnly}
                        aria-label={
                          unreadOnly
                            ? "Show all conversations"
                            : "Show only unread conversations"
                        }
                        style={{
                          border: "1px solid rgba(52, 211, 153, 0.4)",
                          backgroundColor: unreadOnly
                            ? "rgba(52, 211, 153, 0.25)"
                            : "transparent",
                          color: "#a7f3d0",
                          cursor: "pointer",
                          padding: "2px 8px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          borderRadius: "9999px",
                        }}
                      >
                        {unreadOnly ? "Show all" : "Unread only"}
                      </button>
                      <button
                        onClick={handleMarkAllRead}
                        aria-label="Mark all chats as read"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          border: "none",
                          backgroundColor: "transparent",
                          color: "#34d399",
                          cursor: "pointer",
                          padding: "2px 6px",
                          fontSize: "0.8125rem",
                          fontWeight: 600,
                        }}
                      >
                        <CheckCheck style={{ height: "14px", width: "14px" }} />
                        Mark all read
                      </button>
                    </div>
                  </div>
                )}

                {/* Scrollable content */}
                <div style={{ flex: 1, overflowY: "auto" }}>
                  {/* Channels Section */}
                  <div style={sidebarSectionStyle}>
                    <button
                      onClick={() => setChannelsExpanded(!channelsExpanded)}
                      style={{
                        ...sidebarSectionTitleStyle,
                        cursor: "pointer",
                        border: "none",
                        backgroundColor: "transparent",
                        width: "100%",
                      }}
                    >
                      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        {channelsExpanded ? (
                          <ChevronDown style={{ height: "12px", width: "12px" }} />
                        ) : (
                          <ChevronRight style={{ height: "12px", width: "12px" }} />
                        )}
                        Channels
                      </span>
                      <Plus
                        style={{ height: "14px", width: "14px", cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAddChannel(!showAddChannel);
                        }}
                      />
                    </button>
                    {/* Create Channel Form */}
                    {showAddChannel && (
                      <div style={{ padding: "8px 12px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            backgroundColor: "#2c2d31",
                            borderRadius: "6px",
                            overflow: "hidden",
                          }}
                        >
                          <input
                            type="text"
                            placeholder="Channel name"
                            value={newChannelName}
                            onChange={(e) => setNewChannelName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                handleCreateChannel();
                              } else if (e.key === "Escape") {
                                setShowAddChannel(false);
                                setNewChannelName("");
                              }
                            }}
                            style={{
                              flex: 1,
                              padding: "8px 12px",
                              border: "none",
                              outline: "none",
                              backgroundColor: "transparent",
                              color: "#fff",
                              fontSize: "0.875rem",
                            }}
                            autoFocus
                          />
                          <button
                            onClick={handleCreateChannel}
                            disabled={!newChannelName.trim() || createChannel.isPending}
                            style={{
                              padding: "8px 12px",
                              border: "none",
                              backgroundColor: newChannelName.trim() ? "#1164a3" : "transparent",
                              color: newChannelName.trim() ? "#fff" : "#6b7280",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              cursor: newChannelName.trim() ? "pointer" : "default",
                              borderRadius: "0 6px 6px 0",
                            }}
                          >
                            {createChannel.isPending ? "..." : "Add"}
                          </button>
                        </div>
                      </div>
                    )}
                    {channelsExpanded && (
                      <div>
                        {chatGroups
                          ?.filter((channel) => {
                            if (!unreadOnly) return true;
                            return (
                              (channel.unreadCount || 0) > 0 ||
                              !!channel.hasUnreadMessages
                            );
                          })
                          .map((channel) => {
                          const channelHasUnread =
                            (channel.unreadCount || 0) > 0 ||
                            !!channel.hasUnreadMessages;
                          return (
                            <button
                              key={channel.id}
                              onClick={() => handleSelectChannel(channel.id)}
                              style={
                                currentChannelId === channel.id
                                  ? sidebarItemActiveStyle
                                  : sidebarItemStyle
                              }
                              onMouseEnter={(e) => {
                                if (currentChannelId !== channel.id) {
                                  e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.06)";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (currentChannelId !== channel.id) {
                                  e.currentTarget.style.backgroundColor = "transparent";
                                }
                              }}
                            >
                              <Hash style={{ height: "14px", width: "14px", opacity: 0.7 }} />
                              <span
                                style={{
                                  flex: 1,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  fontWeight: channelHasUnread ? 700 : "inherit",
                                }}
                              >
                                {channel.name}
                              </span>
                              {/* Team-channel unread badge — same shape
                                  as the DM one so both surfaces look
                                  consistent when a new message lands. */}
                              {channelHasUnread && (
                                <span style={unreadBadgeStyle}>
                                  {channel.unreadCount || "•"}
                                </span>
                              )}
                            </button>
                          );
                        })}
                        {(!chatGroups || chatGroups.length === 0) && (
                          <div style={{ padding: "0.5rem 1rem", color: "#6b7280", fontSize: "0.8125rem" }}>
                            No channels yet
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Direct Messages Section */}
                  <div style={sidebarSectionStyle}>
                    <button
                      onClick={() => setMessagesExpanded(!messagesExpanded)}
                      style={{
                        ...sidebarSectionTitleStyle,
                        cursor: "pointer",
                        border: "none",
                        backgroundColor: "transparent",
                        width: "100%",
                      }}
                    >
                      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        {messagesExpanded ? (
                          <ChevronDown style={{ height: "12px", width: "12px" }} />
                        ) : (
                          <ChevronRight style={{ height: "12px", width: "12px" }} />
                        )}
                        Direct Messages
                      </span>
                      <Plus
                        style={{ height: "14px", width: "14px", cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAddChat(!showAddChat);
                        }}
                      />
                    </button>
                    {messagesExpanded && (
                      <div>
                        {/* Quick filter input — translucent fill +
                            thin border so it feels embedded in the
                            sidebar instead of a flat dropped-in box. */}
                        <div style={{ padding: "4px 12px 10px" }}>
                          <input
                            type="text"
                            placeholder="Filter staff…"
                            value={staffFilter}
                            onChange={(e) => setStaffFilter(e.target.value)}
                            style={{
                              width: "100%",
                              padding: "8px 12px",
                              border: "1px solid rgba(255, 255, 255, 0.08)",
                              borderRadius: "8px",
                              backgroundColor: "rgba(255, 255, 255, 0.04)",
                              color: "#f3f4f6",
                              fontSize: "0.8125rem",
                              outline: "none",
                              boxSizing: "border-box",
                              transition: "border-color 0.15s ease, background-color 0.15s ease",
                            }}
                            onFocus={(e) => {
                              e.currentTarget.style.borderColor =
                                "rgba(34, 197, 94, 0.5)";
                              e.currentTarget.style.backgroundColor =
                                "rgba(255, 255, 255, 0.08)";
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.borderColor =
                                "rgba(255, 255, 255, 0.08)";
                              e.currentTarget.style.backgroundColor =
                                "rgba(255, 255, 255, 0.04)";
                            }}
                          />
                        </div>
                        {(() => {
                          // Two-line row: avatar (with optional unread
                          // dot overlay) + name/preview stacked +
                          // timestamp/badge on the right. Reads at a
                          // glance like a modern messenger row instead
                          // of the old single-line list.
                          const renderChatRow = (chat) => {
                            const isActive = currentChatId === chat.staffId;
                            const hasUnread =
                              (chat.unreadCount || 0) > 0 ||
                              !!chat.hasUnreadMessages;
                            const inactiveStaff =
                              chat.staffMember?.active === false;
                            const latestMessage = chat.chats?.at(0);
                            const preview = previewTextFor(latestMessage);
                            // Prefer the server-side lastMessageAt so
                            // the timestamp updates even for partners
                            // whose preview message hasn't been pulled
                            // into the lightweight chats[] preview.
                            const timeAgo = chat.lastMessageAt
                              ? formatRelativeTime(chat.lastMessageAt)
                              : latestMessage
                              ? formatRelativeTime(
                                  latestMessage.sentTime ||
                                    latestMessage.createdAt
                                )
                              : "";
                            const avatarUrl = chat.staffMember?.profilePic;

                            return (
                              <button
                                key={chat.staffId}
                                onClick={() => handleSelectChat(chat.staffId)}
                                style={{
                                  ...(isActive
                                    ? sidebarItemActiveStyle
                                    : sidebarItemStyle),
                                  alignItems: "center",
                                  gap: "0.625rem",
                                  padding: "0.5rem 0.625rem",
                                  opacity: inactiveStaff && !isActive ? 0.75 : 1,
                                }}
                                onMouseEnter={(e) => {
                                  if (!isActive) {
                                    e.currentTarget.style.backgroundColor =
                                      "rgba(255, 255, 255, 0.06)";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isActive) {
                                    e.currentTarget.style.backgroundColor =
                                      "transparent";
                                  }
                                }}
                              >
                                {/* Avatar */}
                                <div
                                  style={{
                                    position: "relative",
                                    width: "30px",
                                    height: "30px",
                                    flexShrink: 0,
                                  }}
                                >
                                  {avatarUrl ? (
                                    <img
                                      src={avatarUrl}
                                      alt=""
                                      style={{
                                        width: "30px",
                                        height: "30px",
                                        borderRadius: "50%",
                                        objectFit: "cover",
                                        display: "block",
                                      }}
                                    />
                                  ) : (
                                    <div
                                      style={{
                                        width: "30px",
                                        height: "30px",
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: avatarColorFor(
                                          chat.staffMember?.id
                                        ),
                                        color: "#ffffff",
                                        fontSize: "0.75rem",
                                        fontWeight: 700,
                                        letterSpacing: "0.02em",
                                      }}
                                    >
                                      {initialsFor(chat.staffMember)}
                                    </div>
                                  )}
                                  {hasUnread && (
                                    <span
                                      // Tiny green pip overlay on the
                                      // avatar makes the "new message"
                                      // signal hard to miss without
                                      // pulling the eye off the row.
                                      style={{
                                        position: "absolute",
                                        right: -1,
                                        bottom: -1,
                                        width: "10px",
                                        height: "10px",
                                        borderRadius: "50%",
                                        background: "#22c55e",
                                        border: "2px solid #181b1f",
                                      }}
                                    />
                                  )}
                                </div>

                                {/* Name + preview stack */}
                                <div
                                  style={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    gap: "1px",
                                  }}
                                >
                                  <span
                                    style={{
                                      width: "100%",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      color: isActive
                                        ? "#ffffff"
                                        : hasUnread
                                        ? "#f3f4f6"
                                        : "#d1d5db",
                                      fontSize: "0.875rem",
                                      fontWeight: hasUnread ? 700 : 500,
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {chat.staffMember?.firstName}{" "}
                                    {chat.staffMember?.lastName}
                                  </span>
                                  <span
                                    style={{
                                      width: "100%",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      color: hasUnread ? "#cbd5e1" : "#9ca3af",
                                      fontSize: "0.75rem",
                                      fontWeight: hasUnread ? 500 : 400,
                                      lineHeight: 1.3,
                                    }}
                                  >
                                    {preview || "No messages yet"}
                                  </span>
                                </div>

                                {/* Right column: time + unread pill */}
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-end",
                                    gap: "4px",
                                    flexShrink: 0,
                                    marginLeft: "4px",
                                  }}
                                >
                                  {!!timeAgo && (
                                    <span
                                      style={{
                                        fontSize: "0.6875rem",
                                        color: hasUnread
                                          ? "#86efac"
                                          : "#6b7280",
                                        fontWeight: hasUnread ? 600 : 500,
                                        lineHeight: 1,
                                      }}
                                    >
                                      {timeAgo}
                                    </span>
                                  )}
                                  {hasUnread && (
                                    <span style={unreadBadgeStyle}>
                                      {chat.unreadCount || "•"}
                                    </span>
                                  )}
                                </div>
                              </button>
                            );
                          };

                          // Small sub-header above each section so the
                          // user can see at a glance which bucket a
                          // name belongs to. Tints the Unread header
                          // green to reinforce attention.
                          const subHeader = (label, count) => {
                            const isUnread = label === "Unread";
                            return (
                              <div
                                style={{
                                  padding: "12px 16px 6px",
                                  fontSize: "0.6875rem",
                                  fontWeight: 700,
                                  letterSpacing: "0.08em",
                                  textTransform: "uppercase",
                                  color: isUnread ? "#86efac" : "#a1a7b3",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <span>{label}</span>
                                {count > 0 && (
                                  <span
                                    style={{
                                      fontSize: "0.625rem",
                                      color: isUnread ? "#86efac" : "#9ca3af",
                                      fontWeight: 600,
                                      background: isUnread
                                        ? "rgba(34, 197, 94, 0.15)"
                                        : "rgba(255, 255, 255, 0.06)",
                                      padding: "2px 6px",
                                      borderRadius: "9999px",
                                      letterSpacing: "0.02em",
                                    }}
                                  >
                                    {count}
                                  </span>
                                )}
                              </div>
                            );
                          };

                          const { unread, active, inactive } = filteredChats;
                          const totalShown =
                            unread.length + active.length + inactive.length;

                          if (totalShown === 0) {
                            return (
                              <div
                                style={{
                                  padding: "0.5rem 1rem",
                                  color: "#6b7280",
                                  fontSize: "0.8125rem",
                                }}
                              >
                                {staffFilter
                                  ? "No matching staff"
                                  : "No staff members found"}
                              </div>
                            );
                          }

                          return (
                            <>
                              {unread.length > 0 && (
                                <>
                                  {subHeader("Unread", unread.length)}
                                  {unread.map(renderChatRow)}
                                </>
                              )}
                              {active.length > 0 && (
                                <>
                                  {subHeader("Active", active.length)}
                                  {active.map(renderChatRow)}
                                </>
                              )}
                              {inactive.length > 0 && (
                                <>
                                  {subHeader("Inactive", inactive.length)}
                                  {inactive.map(renderChatRow)}
                                </>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>

                {/* Add new chat form at bottom of sidebar */}
                {showAddChat && (
                  <div style={{ padding: "0.75rem", borderTop: "1px solid #35373b" }}>
                    <AddNewChat addNewChat={addNewChat} chats={chats?.all || []} />
                  </div>
                )}
              </div>

              {/* Main Content Area */}
              <div style={mainContentStyle}>
                {/* Header */}
                <div style={cardHeaderStyle}>
                  <div style={cardTitleStyle}>
                    {currentView === "channel" && <Hash style={{ height: "18px", width: "18px", marginRight: "6px" }} />}
                    {getHeaderTitle()}
                    {/* The previous "Active" pill here read
                        currentChatPartner.active — which is just "is
                        this staff record enabled", not real-time
                        presence. It was misleading (always-on for every
                        enabled user), so we removed it. */}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "4px", position: "relative" }}>
                    {currentView === "channel" && (
                      <button
                        style={{
                          border: "none",
                          backgroundColor: showChannelMembers ? "#f3f4f6" : "transparent",
                          cursor: "pointer",
                          padding: "6px",
                          display: "flex",
                          alignItems: "center",
                          borderRadius: "4px",
                        }}
                        onClick={() => setShowChannelMembers(!showChannelMembers)}
                        aria-label="Manage members"
                        title="Manage members"
                      >
                        <Users style={{ height: "18px", width: "18px", color: "#6b7280" }} />
                      </button>
                    )}
                    <button
                      style={{
                        border: "none",
                        backgroundColor: "transparent",
                        cursor: "pointer",
                        padding: "6px",
                        display: "flex",
                        alignItems: "center",
                        borderRadius: "4px",
                      }}
                      onClick={() => setIsOpen(false)}
                      aria-label="Close chat"
                    >
                      <X style={{ height: "18px", width: "18px", color: "#6b7280" }} />
                    </button>
                    {/* Channel Members Panel */}
                    {showChannelMembers && currentView === "channel" && (
                      <ChannelMembers
                        channelId={currentChannelId}
                        channelName={currentChannel?.name}
                        onClose={() => setShowChannelMembers(false)}
                      />
                    )}
                  </div>
                </div>

                {/* Content */}
                <div style={cardContentStyle}>
                  {isSearchOpen ? (
                    <SearchMessages onSelectMessage={(chatId) => handleSelectChat(chatId)} />
                  ) : currentView === "welcome" ? (
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "2.5rem 2rem",
                        textAlign: "center",
                        background:
                          "radial-gradient(circle at 50% 0%, rgba(34, 197, 94, 0.08) 0%, rgba(34, 197, 94, 0) 60%), #fafafa",
                      }}
                    >
                      <div
                        style={{
                          width: "72px",
                          height: "72px",
                          borderRadius: "50%",
                          background:
                            "linear-gradient(140deg, #008056 0%, #006546 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: "1.25rem",
                          boxShadow:
                            "0 12px 24px -8px rgba(0, 101, 70, 0.35)",
                        }}
                      >
                        <MessageCircle
                          style={{
                            height: "32px",
                            width: "32px",
                            color: "#ffffff",
                          }}
                          strokeWidth={2.5}
                        />
                      </div>
                      <h3
                        style={{
                          margin: "0 0 0.5rem",
                          color: "#111827",
                          fontSize: "1.125rem",
                          fontWeight: 600,
                          letterSpacing: "-0.01em",
                        }}
                      >
                        Team Chat
                      </h3>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "0.875rem",
                          color: "#6b7280",
                          maxWidth: "260px",
                          lineHeight: 1.5,
                        }}
                      >
                        Pick a conversation or channel from the left to start
                        messaging your team.
                      </p>
                      {totalUnreadCount > 0 && (
                        <div
                          style={{
                            marginTop: "1.25rem",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "6px 12px",
                            borderRadius: "9999px",
                            background: "rgba(34, 197, 94, 0.12)",
                            color: "#15803d",
                            fontSize: "0.8125rem",
                            fontWeight: 600,
                          }}
                        >
                          <span
                            style={{
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              background: "#22c55e",
                            }}
                          />
                          {totalUnreadCount} unread{" "}
                          {totalUnreadCount === 1 ? "message" : "messages"}
                        </div>
                      )}
                    </div>
                  ) : currentView === "channel" ? (
                    <ChatMessages
                      channelId={currentChannelId}
                      currentView={currentView}
                      onCreateTask={setTaskPanelData}
                    />
                  ) : (
                    <ChatMessages
                      activeChatStaffId={currentChatId}
                      currentView={currentView}
                      onCreateTask={setTaskPanelData}
                    />
                  )}
                </div>

                {/* Message Input */}
                {(currentView === "chat" || currentView === "channel") && (
                  <ChatSendMessageForm
                    chatStaffMemberId={currentView === "chat" ? currentChatId : null}
                    channelId={currentView === "channel" ? currentChannelId : null}
                  />
                )}
              </div>

              {/* Task Panel - slides in from right when creating task from message */}
              {taskPanelData && (
                <div
                  style={{
                    width: "380px",
                    flexShrink: 0,
                    borderLeft: "1px solid #e5e7eb",
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: "#fff",
                    overflow: "hidden",
                    minHeight: 0,
                  }}
                >
                  {/* Task Panel Header */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "10px 16px",
                      borderBottom: "1px solid #e5e7eb",
                      backgroundColor: "#f9fafb",
                      flexShrink: 0,
                    }}
                  >
                    <span style={{ fontWeight: 600, fontSize: "0.875rem", color: "#111827" }}>
                      Create Task
                    </span>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <button
                        type="button"
                        form="chat-task-form"
                        onClick={() => {
                          const form = document.getElementById("chat-task-form");
                          if (form) form.requestSubmit();
                        }}
                        style={{
                          border: "none",
                          backgroundColor: "#008056",
                          color: "#fff",
                          cursor: "pointer",
                          padding: "6px 14px",
                          fontSize: "0.8125rem",
                          fontWeight: 600,
                          borderRadius: "6px",
                        }}
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setTaskPanelData(null)}
                        style={{
                          border: "none",
                          backgroundColor: "transparent",
                          cursor: "pointer",
                          padding: "4px",
                          display: "flex",
                          alignItems: "center",
                          borderRadius: "4px",
                        }}
                        aria-label="Close task panel"
                      >
                        <X style={{ height: "18px", width: "18px", color: "#6b7280" }} />
                      </button>
                    </div>
                  </div>

                  {/* Task Form - scrollable */}
                  <div
                    style={{
                      flex: 1,
                      overflowY: "auto",
                      padding: "12px 16px",
                      minHeight: 0,
                    }}
                  >
                    <AddTaskForm
                      formId="chat-task-form"
                      staffMemberId={taskPanelData.assigneeId}
                      initialTaskDescription={taskPanelData.message?.message}
                      closeModal={() => setTaskPanelData(null)}
                      compact
                    />
                  </div>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
